import { Box, Typography } from '@mui/material';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { FC, useMemo } from 'react';
import { vyDate } from '@vy/utils';
import { formatNumberAbbreviated } from '../../../../../utils/number';

export type RevenueTrendChartDataset = {
  month: Date;
  arr: number;
  mrr: number;
};

export type RevenueTrendsChartProps = {
  dataset: RevenueTrendChartDataset[];
  chartHeight: number;
};
const CHART_MARGINS = { left: 40, right: 16, top: 24, bottom: 32 };
const AXIS_TEXT_COLOR = '#A4ADBE';
const AXIS_FONT_SIZE = 12;
const BAR_COLOR = '#5083FF';
const PLACEHOLDER_MONTHS_COUNT = 6;
const EMPTY_STATE_TOP_OFFSET = '17px';

export const RevenueTrendsChart: FC<RevenueTrendsChartProps> = ({ dataset, chartHeight }) => {
  const normalizedDataset = useMemo(() => {
    return (dataset ?? []).map((dataPoint) => {
      const monthDate = vyDate(dataPoint.month);

      return {
        ...dataPoint,
        arr: dataPoint.arr ?? dataPoint.mrr * 12,
        month: monthDate.toDate(),
        monthLabel: monthDate.format('MMM'),
      };
    });
  }, [dataset]);

  const placeholderDataset = useMemo(() => {
    const currentMonthStart = vyDate().startOf('month');

    return Array.from({ length: PLACEHOLDER_MONTHS_COUNT }, (_, index) => {
      const monthDate = currentMonthStart.clone().subtract(PLACEHOLDER_MONTHS_COUNT - index, 'months');

      return {
        month: monthDate.toDate(),
        monthLabel: monthDate.format('MMM'),
        arr: 0,
        mrr: 0,
      };
    });
  }, []);

  const isEmpty = normalizedDataset.length === 0;
  const chartDataset = isEmpty ? placeholderDataset : normalizedDataset;

  return (
    <Box
      sx={{
        position: "relative",
        width: '100%'
      }}>
      <ChartsContainer
        height={chartHeight}
        dataset={chartDataset}
        margin={CHART_MARGINS}
        sx={{
          [`.${axisClasses.root}`]: {
            [`.${axisClasses.tick}, .${axisClasses.line}`]: {
              stroke: 'none',
            },
          },
          '.MuiChartsGrid-line': {
            strokeDasharray: '4 4',
          },
        }}
        xAxis={[
          {
            scaleType: 'band',
            categoryGapRatio: 0.7,
            dataKey: 'monthLabel',
            tickLabelStyle: {
              fill: AXIS_TEXT_COLOR,
              fontSize: AXIS_FONT_SIZE,
              fontFamily: 'Inter, sans-serif',
            },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value: number) => formatNumberAbbreviated(value),
            tickLabelStyle: {
              fill: AXIS_TEXT_COLOR,
              fontSize: AXIS_FONT_SIZE,
              fontFamily: 'Inter, sans-serif',
            },
          },
        ]}
        series={[
          {
            type: 'bar',
            dataKey: 'arr',
            label: 'ARR',
            color: BAR_COLOR,
          },
        ]}
      >
        <ChartsGrid horizontal />
        <BarPlot borderRadius={4} />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip />
      </ChartsContainer>
      {isEmpty && (
        <Typography
          sx={{
            fontSize: AXIS_FONT_SIZE,
            color: "text.primary",
            position: 'absolute',
            inset: 0,
            top: EMPTY_STATE_TOP_OFFSET,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
            textAlign: 'center'
          }}>
          No data to show yet
        </Typography>
      )}
    </Box>
  );
};
